import { useState, useMemo, useCallback, useEffect } from 'react';
import Pagination from '@/components/base/Pagination';
import { supabase } from '@/lib/supabase';
import type { RegistoSiadap, AvaliacaoSiadap } from '@/context/SiadapContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { MUNICIPIO_SIADAP_ID } from '@/lib/municipios';
import SiadapRow from './components/SiadapRow';
import SiadapFormModal from './components/SiadapFormModal';
import SiadapDeleteModal from './components/SiadapDeleteModal';
import FuncionarioHistoricoModal from './components/FuncionarioHistoricoModal';

const avaliacoes: AvaliacaoSiadap[] = ['Excelente', 'Muito Bom', 'Bom', 'Regular', 'Inadequado'];
const avaliacoesAntigas: AvaliacaoSiadap[] = ['Relevante', 'Adequado'];

const avaliacaoConfig: Record<string, { bg: string; text: string; dot: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  Bom:         { bg: 'bg-slate-100',  text: 'text-slate-600',   dot: 'bg-slate-400' },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400' },
  Inadequado:  { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
  Relevante:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-600' },
  Adequado:    { bg: 'bg-slate-100',  text: 'text-slate-600',   dot: 'bg-slate-500' },
};

type SiadapSortKey = 'funcionario' | 'periodo' | 'avaliacao' | 'pontosChefe' | 'pontosCCA' | 'nivel' | 'remuneracao';
type SiadapSortDir = 'asc' | 'desc';

const AVAL_ORDER: Record<string, number> = {
  Excelente: 0, 'Muito Bom': 1, Bom: 2, Relevante: 3, Adequado: 4, Regular: 5, Inadequado: 6,
};

function anoFinalPeriodo(periodo: string): number {
  const partes = (periodo ?? '').trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  return isNaN(ano) ? 9999 : ano;
}

export default function SiadapPage() {
  const { funcionarios } = useFuncionarios();
  const { registosSiadap, loadingSiadap, reloadSiadap } = useSiadap();
  const [search, setSearch] = useState('');
  const [avaliacaoFilter, setAvaliacaoFilter] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [periodoFilter, setPeriodoFilter] = useState<'todos' | 'antes2025' | 'apos2025'>('todos');
  const [formModal, setFormModal] = useState<{ open: boolean; registo?: RegistoSiadap | null }>({ open: false });
  const [deleteModal, setDeleteModal] = useState<RegistoSiadap | null>(null);
  const [historicoFuncionario, setHistoricoFuncionario] = useState<Funcionario | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState<SiadapSortKey>('funcionario');
  const [sortDir, setSortDir] = useState<SiadapSortDir>('asc');
  const [modoLote, setModoLote] = useState(false);

  // Carregar preferência do modo lote do localStorage
  useEffect(() => {
    const saved = localStorage.getItem('siadap_modo_lote');
    if (saved === 'true') setModoLote(true);
  }, []);

  // Guardar preferência quando muda
  useEffect(() => {
    localStorage.setItem('siadap_modo_lote', String(modoLote));
  }, [modoLote]);

  const registos = registosSiadap;
  const loadingData = loadingSiadap;

  const carreiras = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.carreira))).sort(), [funcionarios]);

  function handleSort(key: SiadapSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }

  const filtered = useMemo(() => {
    const base = registos.filter((r) => {
      const func = funcionarios.find((f) => f.id === r.funcionarioId);
      const matchSearch = search === '' || (func?.nome ?? '').toLowerCase().includes(search.toLowerCase()) || r.funcionarioId.includes(search);
      const matchAval = avaliacaoFilter === '' || r.avaliacao === avaliacaoFilter;
      const matchCarreira = carreiraFilter === '' || func?.carreira === carreiraFilter;
      const anoFinal = anoFinalPeriodo(r.periodoAvaliacao);
      const matchPeriodo =
        periodoFilter === 'todos' ||
        (periodoFilter === 'antes2025' && anoFinal < 2025) ||
        (periodoFilter === 'apos2025' && anoFinal >= 2025);
      return matchSearch && matchAval && matchCarreira && matchPeriodo;
    });

    return [...base].sort((a, b) => {
      const funcA = funcionarios.find((f) => f.id === a.funcionarioId);
      const funcB = funcionarios.find((f) => f.id === b.funcionarioId);
      let cmp = 0;
      switch (sortKey) {
        case 'funcionario':
          cmp = (funcA?.nome ?? '').localeCompare(funcB?.nome ?? '');
          break;
        case 'periodo':
          cmp = anoFinalPeriodo(a.periodoAvaliacao) - anoFinalPeriodo(b.periodoAvaliacao);
          break;
        case 'avaliacao':
          cmp = (AVAL_ORDER[a.avaliacao] ?? 99) - (AVAL_ORDER[b.avaliacao] ?? 99);
          break;
        case 'pontosChefe':
          cmp = a.pontosChefe - b.pontosChefe;
          break;
        case 'pontosCCA':
          cmp = a.pontosCCA - b.pontosCCA;
          break;
        case 'nivel':
          cmp = (a.nivel ?? 0) - (b.nivel ?? 0);
          break;
        case 'remuneracao':
          cmp = a.remuneracaoAtual - b.remuneracaoAtual;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [registos, search, avaliacaoFilter, carreiraFilter, periodoFilter, funcionarios, sortKey, sortDir]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, avaliacaoFilter, carreiraFilter, periodoFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function showSuccess(msg: string) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); }

  const handleSave = useCallback(async (data: RegistoSiadap) => {
    if (saving) return;
    setSaving(true);
    try {
      const isEditing = !!formModal.registo;
      const row = {
        funcionario_id: data.funcionarioId,
        periodo_avaliacao: data.periodoAvaliacao.trim(),
        avaliacao: data.avaliacao,
        pontos_chefe: data.pontosChefe,
        pontos_cca: data.pontosCCA,
        descricao_subida: data.descricaoSubida,
        nivel: data.nivel,
        remuneracao_atual: data.remuneracaoAtual,
        updated_at: new Date().toISOString(),
        municipio_id: MUNICIPIO_SIADAP_ID,
      };
      if (isEditing) {
        const { error } = await supabase.from('registos_siadap').update(row).eq('id', data.id);
        if (error) throw error;
        showSuccess('Registo atualizado com sucesso!');
      } else {
        const { data: existente } = await supabase
          .from('registos_siadap')
          .select('id')
          .eq('funcionario_id', data.funcionarioId)
          .ilike('periodo_avaliacao', data.periodoAvaliacao.trim())
          .maybeSingle();
        if (existente) {
          showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
          return;
        }
        const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : 's' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
        const { error } = await supabase.from('registos_siadap').insert({ id: newId, ...row });
        if (error) {
          if ((error as { code?: string }).code === '23505') {
            showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
            return;
          }
          throw error;
        }
        showSuccess('Registo adicionado com sucesso!');
      }
      await reloadSiadap();
      setFormModal({ open: false });
    } catch (err) {
      console.error('Erro ao guardar registo SIADAP:', err);
      showSuccess('Erro ao guardar registo. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }, [saving, formModal.registo, reloadSiadap]);

  async function handleSaveAndContinue(data: RegistoSiadap) {
    if (saving) return;
    setSaving(true);
    try {
      const { data: existente } = await supabase
        .from('registos_siadap')
        .select('id')
        .eq('funcionario_id', data.funcionarioId)
        .ilike('periodo_avaliacao', data.periodoAvaliacao.trim())
        .maybeSingle();
      if (existente) {
        showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
        return;
      }
      const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 's' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
      const { error } = await supabase.from('registos_siadap').insert({
        id: newId,
        funcionario_id: data.funcionarioId,
        periodo_avaliacao: data.periodoAvaliacao.trim(),
        avaliacao: data.avaliacao,
        pontos_chefe: data.pontosChefe,
        pontos_cca: data.pontosCCA,
        descricao_subida: data.descricaoSubida,
        nivel: data.nivel,
        remuneracao_atual: data.remuneracaoAtual,
        updated_at: new Date().toISOString(),
        municipio_id: MUNICIPIO_SIADAP_ID,
      });
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
          return;
        }
        throw error;
      }
      await reloadSiadap();
      showSuccess('Registo adicionado! Pode continuar a inserir mais avaliações.');
    } catch (err) {
      console.error('Erro ao guardar registo SIADAP:', err);
      showSuccess('Erro ao guardar registo. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteModal) return;
    setSaving(true);
    try {
      await supabase.from('registos_siadap').delete().eq('id', deleteModal.id);
      await reloadSiadap();
      showSuccess('Registo eliminado.');
    } catch {
      showSuccess('Erro ao eliminar.');
    } finally {
      setSaving(false);
      setDeleteModal(null);
    }
  }

  const mediaChefe = registos.length > 0 ? (registos.reduce((s, r) => s + r.pontosChefe, 0) / registos.length).toFixed(2) : '—';
  const mediaCCA = registos.length > 0 ? (registos.reduce((s, r) => s + r.pontosCCA, 0) / registos.length).toFixed(2) : '—';
  const mediaRem = registos.length > 0 ? registos.reduce((s, r) => s + r.remuneracaoAtual, 0) / registos.length : 0;

  const siadapHeaders: { key: SiadapSortKey | null; label: string }[] = [
    { key: 'funcionario', label: 'Funcionário' },
    { key: 'periodo', label: 'Período' },
    { key: 'avaliacao', label: 'Avaliação' },
    { key: 'pontosChefe', label: 'Pont. Chefia' },
    { key: 'pontosCCA', label: 'Pont. CCA' },
    { key: 'nivel', label: 'Nível' },
    { key: 'remuneracao', label: 'Remuneração' },
    { key: null, label: 'Ações' },
  ];

  return (
    <>
      <main className="flex-1 px-8 py-6 max-w-[1400px] w-full mx-auto">
        {/* PAGE HEADER */}
        <div className="mb-7">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-award-line text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Avaliação de Desempenho</span>
                </div>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">SIADAP</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Registos de avaliação — período, pontuações, nível e remuneração</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Toggle Modo Lote */}
              <button
                onClick={() => setModoLote((v) => !v)}
                className={`whitespace-nowrap flex items-center gap-2 text-[13px] font-medium px-3 py-2.5 rounded-lg transition-colors cursor-pointer border ${
                  modoLote
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'bg-white border-zinc-200 text-zinc-500 hover:text-zinc-700'
                }`}
                title={modoLote ? 'Modo lote ativo — cada guardar abre novo registo' : 'Ativar modo lote para inserir vários registos seguidos'}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${modoLote ? 'ri-stack-line' : 'ri-stack-line'} text-sm`} />
                </div>
                <span className="hidden sm:inline">Modo Lote</span>
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${modoLote ? 'bg-emerald-200 text-emerald-800' : 'bg-zinc-100 text-zinc-400'}`}>
                  {modoLote ? 'ON' : 'OFF'}
                </span>
              </button>
              <button
                onClick={() => setFormModal({ open: true, registo: null })}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                Novo Registo
              </button>
            </div>
          </div>
        </div>

        {successMsg && (
          <div className="mb-5 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
            {successMsg}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { icon: 'ri-award-line', value: registos.length, label: 'Total de Registos', iconBg: 'bg-zinc-100', iconColor: 'text-zinc-600' },
            { icon: 'ri-user-star-line', value: mediaChefe, label: 'Média Pont. Chefia', iconBg: 'bg-zinc-100', iconColor: 'text-zinc-500' },
            { icon: 'ri-shield-check-line', value: mediaCCA, label: 'Média Pont. CCA', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
            { icon: 'ri-money-euro-circle-line', value: mediaRem > 0 ? mediaRem.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—', label: 'Remuneração Média', iconBg: 'bg-amber-50', iconColor: 'text-amber-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg} ${s.iconColor}`}>
                <i className={`${s.icon} text-lg`} />
              </div>
              <div>
                <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-zinc-100 px-5 py-4 mb-5">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Distribuição por Avaliação</p>
          <div className="flex flex-wrap gap-2">
            {avaliacoes.map((a) => {
              const count = registos.filter((r) => r.avaliacao === a).length;
              if (count === 0) return null;
              const pct = registos.length > 0 ? Math.round((count / registos.length) * 100) : 0;
              const cfg = avaliacaoConfig[a];
              return (
                <div key={a} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${cfg.bg}`}>
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={`text-xs font-medium ${cfg.text}`}>{a}</span>
                  <span className={`text-xs font-bold ${cfg.text}`}>{count}</span>
                  <span className={`text-xs ${cfg.text} opacity-60`}>({pct}%)</span>
                </div>
              );
            })}
            {avaliacoesAntigas.map((a) => {
              const count = registos.filter((r) => r.avaliacao === a).length;
              if (count === 0) return null;
              const pct = registos.length > 0 ? Math.round((count / registos.length) * 100) : 0;
              const cfg = avaliacaoConfig[a];
              return (
                <div key={a} className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed ${cfg.bg}`} title="Escala anterior a 2025">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={`text-xs font-medium ${cfg.text}`}>{a}</span>
                  <span className={`text-xs font-bold ${cfg.text}`}>{count}</span>
                  <span className={`text-xs ${cfg.text} opacity-60`}>({pct}%)</span>
                  <span className="text-xs text-slate-400 italic">ant.</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filtro por período */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center bg-white border border-zinc-200 rounded-lg p-1 gap-1">
            {([
              { value: 'todos', label: 'Todos', icon: 'ri-calendar-2-line' },
              { value: 'antes2025', label: 'Anterior a 2025', icon: 'ri-history-line' },
              { value: 'apos2025', label: 'A partir de 2025', icon: 'ri-calendar-check-line' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriodoFilter(opt.value)}
                className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all cursor-pointer ${
                  periodoFilter === opt.value
                    ? opt.value === 'antes2025'
                      ? 'bg-amber-500 text-white'
                      : opt.value === 'apos2025'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-900 text-white'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <i className={`${opt.icon} text-xs`} />
                {opt.label}
                {opt.value !== 'todos' && (
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                    periodoFilter === opt.value ? 'bg-white/20' : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {opt.value === 'antes2025'
                      ? registos.filter((r) => anoFinalPeriodo(r.periodoAvaliacao) < 2025).length
                      : registos.filter((r) => anoFinalPeriodo(r.periodoAvaliacao) >= 2025).length}
                  </span>
                )}
              </button>
            ))}
          </div>
          {periodoFilter !== 'todos' && (
            <div className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg ${
              periodoFilter === 'antes2025' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}>
              <i className={`${periodoFilter === 'antes2025' ? 'ri-information-line' : 'ri-checkbox-circle-line'} text-sm`} />
              {periodoFilter === 'antes2025'
                ? 'Excelente = 6 pts · Muito Bom = 4 pts · Bom = 3 pts · Regular = 2 pts'
                : 'Excelente = 3 pts · Muito Bom = 2 pts · Bom = 1,5 pts · Regular = 1 pt'}
            </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-2.5 mb-4">
          <div className="relative flex-1">
            <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar por nome ou nº..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300"
            />
          </div>
          <select
            value={avaliacaoFilter}
            onChange={(e) => setAvaliacaoFilter(e.target.value)}
            className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-300 cursor-pointer"
          >
            <option value="">Todas as Avaliações</option>
            {avaliacoes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={carreiraFilter}
            onChange={(e) => setCarreiraFilter(e.target.value)}
            className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-300 cursor-pointer"
          >
            <option value="">Todas as Carreiras</option>
            {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <p className="text-[11px] text-zinc-400 mb-3">
          <span className="font-semibold text-zinc-600">{filtered.length}</span> registo(s)
          {periodoFilter !== 'todos' && (
            <span className="ml-2 text-zinc-500">
              · filtrado por{' '}
              <span className="font-semibold">
                {periodoFilter === 'antes2025' ? 'escala anterior a 2025' : 'escala atual (≥ 2025)'}
              </span>
            </span>
          )}
        </p>

        <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/60">
                  {siadapHeaders.map((col, ci) => (
                    <th
                      key={ci}
                      onClick={() => col.key && handleSort(col.key)}
                      className={`px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap select-none group ${
                        col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                      } ${
                        sortKey === col.key ? 'text-zinc-700' : 'text-zinc-400'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {col.label}
                        {col.key && (
                          sortKey === col.key
                            ? <i className={`${sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[13px] text-zinc-700`} />
                            : <i className="ri-expand-up-down-line text-[11px] text-zinc-300 group-hover:text-zinc-400" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingData ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-2 text-zinc-300">
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-loader-4-line text-3xl animate-spin" />
                        </div>
                        <p className="text-[13px] text-zinc-400">A carregar registos...</p>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-2 text-zinc-300">
                        <div className="w-10 h-10 flex items-center justify-center">
                          <i className="ri-search-line text-3xl" />
                        </div>
                        <p className="text-[13px] text-zinc-400">Nenhum registo encontrado.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((r) => (
                    <SiadapRow
                      key={r.id}
                      registo={r}
                      funcionarios={funcionarios}
                      onEdit={() => setFormModal({ open: true, registo: r })}
                      onDelete={() => setDeleteModal(r)}
                      onNameClick={(func) => setHistoricoFuncionario(func)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINAÇÃO */}
        {!loadingData && (
          <div className="mt-4">
            <Pagination
              currentPage={safePage}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              itemLabel="registo(s)"
            />
          </div>
        )}
      </main>

      {formModal.open && (
        <SiadapFormModal
          registo={formModal.registo}
          modoLote={modoLote}
          onSave={handleSave}
          onSaveAndContinue={handleSaveAndContinue}
          onClose={() => setFormModal({ open: false })}
        />
      )}
      {deleteModal && (
        <SiadapDeleteModal
          registo={deleteModal}
          onConfirm={handleDelete}
          onClose={() => setDeleteModal(null)}
        />
      )}
      {historicoFuncionario && (
        <FuncionarioHistoricoModal
          funcionario={historicoFuncionario}
          onClose={() => setHistoricoFuncionario(null)}
        />
      )}
      {saving && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/10 pointer-events-none">
          <div className="bg-white rounded-xl px-6 py-4 flex items-center gap-3 text-slate-700 text-sm shadow-sm pointer-events-none">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-lg" />
            </div>
            A guardar...
          </div>
        </div>
      )}
    </>
  );
}
